import Image from 'next/image'

import styles from './styles.module.scss'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookmark, faEye, faMessage, faPaperPlane, faShare, faXmark } from '@fortawesome/free-solid-svg-icons'

import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

import AuctionListing from '../AuctionListing/AuctionListing'
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner'

import { useSocket } from '../../../Context/socket'
import { useSession } from 'next-auth/react'
import axios from 'axios'
import { BarLoader, PacmanLoader, PuffLoader, PulseLoader } from 'react-spinners'
import errorMsg from '../../../Functions/errorMsg'
import successMsg from '../../../Functions/successMsg'
import { isEqual } from 'lodash'

import NumberFormat from 'react-number-format';
import { useRouter } from 'next/router'

export default function Winnings({ setNavCategory, setPendingRequest }) {

  const { socket } = useSocket();
  const { data: session, status } = useSession();

  const [initialLoading, setInitialLoading] = useState(null);
  const [winningListings, setWinningListings] = useState(null);

  useEffect(() => {
    if(initialLoading !== null) return;
    if(winningListings !== null) return;
    if(!session) return;
    if(status === 'loading') return;

    async function getMyListings() {
        setInitialLoading(true);
        setPendingRequest(true);

        const result = await axios.post(`${window.location.origin}/api/auctionHub/getWinnings`, {
          getRequestingID: session.user.token.id
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        })

        if(result && result.status === 200) {
          const data = result.data;
          setWinningListings(data); // Sort by unclaimed winnings - purchaseDetails that are null are held first
        }

        setPendingRequest(false);
    }

    getMyListings();

  }, [session, status])

  function isLoading() {
    if(status === 'loading') return true;
    if(initialLoading === false || initialLoading === null) return true;
    if(winningListings === null) return true;
    return false;
  }

  const [viewListingData, setViewListingData] = useState(null);

  return (
    <section id={styles.wrapper}>
      { viewListingData !== null && <AuctionListing socket={socket} listingID={viewListingData} closeListing={setViewListingData} /> }

      { isLoading() === true && <section id={styles.loading}><PulseLoader size={30} color='#154734' /></section> }

      { isLoading() === false && (winningListings.length === 0 ?
        <div id={styles.noListings}>
          <h1>You have no winnings.</h1>
        </div>
        :
        <section id={styles.container}>
          {
            winningListings.map((listing, index) => {
              return <ListingCard key={index} listing={listing} setViewListingData={setViewListingData} />
            })
          }
        </section>
      )}

  </section>
  )
}

const ListingCard = ({ listing, setViewListingData }) => {

  const { data: session, status } = useSession();
  const { socket } = useSocket();
  const router = useRouter();

  const listingID = listing._id;

  const [coverLoaded, setCoverLoaded] = useState(false);
  const getCover = listing.images.findIndex((images) => images.position === 1);

  /* 'listing' data object structure:

    _id: listing._id,
    owner: listing.owner,
    onlinePayment: listing.acceptPayments.enabled,
    purchaseDetails: listing.purchaseDetails,
    images: listing.images

  */
    const [pendingHandleMessageOperation, setPendingHandleMessageOperation] = useState(false);
    const [openNewMessage, setOpenNewMessage] = useState(null);
  
    async function handleMessageSellerOperation() {
      if(pendingHandleMessageOperation) return;
      if(!session) return;
      if(status !== 'authenticated') return;
  
      setPendingHandleMessageOperation(true);
  
      const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
          requestingUserID: session.user.token.id,
          otherUser: listing.owner,
          convoType: 'Auctions',
          listingID: listingID,
      }).catch((error) => {
          errorMsg(error?.response?.data?.status || error.toString());
          closeProfile();
      })
  
      if(result && result.status === 200) {
          const data = result.data;
  
          if(data.hasConvo) {
            handleOpenCurrentConversation(data.convoID);
          } else {
            setOpenNewMessage(true);
          }
  
      }
  
      setPendingHandleMessageOperation(false);
  
    }
  
    function handleOpenCurrentConversation(convoID) {
      const { query } = router;
  
      const updatedQuery = { ...query, openConversationID: convoID };
  
      router.push({ pathname: router.pathname, query: updatedQuery });
    }
  
    const [pendingSend, setPendingSend] = useState(false);
    const messageRef = useRef(null);
  
    async function handleSendMessage(e) {
      e.preventDefault();
      if(status === 'loading') return;
      if(!socket) return;
      if(!session) return errorMsg("Please login to message this user.");
      if(status === 'unauthenticated') return errorMsg("Please login to message this user.");
      if(pendingSend === true) return;
      if(!messageRef || !messageRef.current) return;
      const messageText = messageRef.current.value;
      if(messageText.length === 0) return errorMsg("Please enter your message before sending.");
      if(messageText.length > 500) return errorMsg("Message cannot be over 500 characters.")
      
      const senderID = session.user.token.id;
      const sender = session.user.token.username;
      const toUser = listing.owner;

      if(!toUser) return errorMsg("Could not find the buyer, please contact us if the issue persists.");
  
      const basicConvoData = {
          type: 'Auctions',
          listingID: listingID,
          receiver: toUser,
          sender: sender,
          creationDate: new Date().toISOString()
      }
  
      setPendingSend(true);
  
      const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
          conversationData: basicConvoData,
          messageText: messageText,
          senderID: senderID
      }).catch((error) => {
          errorMsg(error?.response?.data?.status || error.toString())
      });
  
      if(result && result.status === 200) {
          const newConvoData = result.data;
  
          socket.emit('createNewConversation', newConvoData)
  
          successMsg("Your message was sent.");
  
          setOpenNewMessage(false);
      }
  
      setPendingSend(false);
    }

    function getPaymentStatus() {
      if(listing.purchaseDetails === null) return 'Transaction Awaits';
      if(listing.purchaseDetails.status === 'SOLD') return 'Purchase Complete';
      if(listing.purchaseDetails.status === 'UNSOLD') return 'Transaction Compromise';
      if(listing.purchaseDetails.status === 'CANCELLATION') return 'Transaction Canceled';
    }

    function returnPaymentStatusContainerStyle() {
      if(getPaymentStatus() === 'Transaction Awaits') return { backgroundColor: '#FFB81C', color: '#000000' }
      if(getPaymentStatus() === 'Purchase Complete') return { backgroundColor: '#0DA00D', color: '#FFFFFF' }
      if(getPaymentStatus() === 'Transaction Compromise') return { backgroundColor: '#CA0B00', color: '#FFFFFF' }
      if(getPaymentStatus() === 'Transaction Canceled') return { backgroundColor: '#CA0B00', color: '#FFFFFF' }
    }


  return (
    <div className={styles.card}>

      <div style={{display: openNewMessage ? 'flex' : 'none'}} id={styles.quickMessage}>
        <FontAwesomeIcon onClick={() => setOpenNewMessage(false)} icon={faXmark} id={styles.exit} />
        <h1>Chat with {listing.owner}</h1>
        <textarea ref={messageRef} placeholder="Write this user a message..." />
        <button onClick={handleSendMessage}> {pendingSend ? <BarLoader color='#4169E1' /> : <FontAwesomeIcon icon={faPaperPlane} /> } </button>
      </div>

      <section>
        <div style={{display: coverLoaded === false ? 'flex' : 'none'}}> <LoadingSpinner type="pulse" color="#154734" /></div>
        <Image sizes='auto auto' fill src={listing.images[getCover].url} alt={listing.images[getCover].url} onLoadingComplete={() => setCoverLoaded(true)} />
        <h1 style={returnPaymentStatusContainerStyle()}>{getPaymentStatus()}</h1>
      </section>
      <section>
        <div>
          <h1>{listing.title}</h1>
          <h2>Purchase Total: <b>${parseFloat(listing.purchaseAmount).toLocaleString()}</b></h2>
          <h3>Meetup Preference: <b>{listing.meetupPref}</b></h3>
        </div>

        <div>
          <motion.button onClick={() => setViewListingData(listingID)}><FontAwesomeIcon icon={faEye} id={styles.icon} /></motion.button>
          <motion.button onClick={handleMessageSellerOperation}> { pendingHandleMessageOperation ? <PuffLoader size={22} color='white' /> : <FontAwesomeIcon icon={faMessage} id={styles.icon} /> }  </motion.button>
        </div>
      </section>
    </div>
  )
}